/**
 * Act1ResultScreen — 第一幕结果展示
 *
 * 显示亲友态度最终值、结局判定、高光时刻
 */
import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, ArrowRight, Heart } from 'lucide-react';
import { useRootStore } from '../../stores/rootStore';
import { useAct2Store } from '../../stores/act2Store';
import type { Act1Outcome } from '@shared/types/gameState';

const OUTCOME_CONFIG: Record<Act1Outcome, { icon: React.ReactNode; color: string; label: string }> = {
  '顺利通关': {
    icon: <CheckCircle size={32} className="text-green-500" />,
    color: 'from-green-50 to-emerald-50 border-green-200',
    label: '亲友团一致通过！',
  },
  '勉强通关': {
    icon: <AlertTriangle size={32} className="text-amber-500" />,
    color: 'from-amber-50 to-yellow-50 border-amber-200',
    label: '勉强过关，但有隐患...',
  },
  '家庭反对': {
    icon: <XCircle size={32} className="text-red-500" />,
    color: 'from-red-50 to-pink-50 border-red-200',
    label: '亲友团强烈反对！',
  },
};

export const Act1ResultScreen: React.FC = () => {
  const root = useRootStore();
  const act2 = useAct2Store();
  const result = root.act1Result;

  if (!result || !root.protagonistA || !root.protagonistB) return null;

  const config = OUTCOME_CONFIG[result.outcome];
  const canContinue = result.outcome !== '家庭反对';

  const handleContinue = () => {
    if (canContinue) {
      act2.initialize(result!);
      root.startAct2Setup();
    } else {
      root.resetAll();
    }
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-cream to-[#F5EDE3]">
      <div className={`max-w-md w-full bg-gradient-to-br ${config.color} border rounded-2xl p-6 shadow-lg`}>
        {/* Outcome icon + label */}
        <div className="flex flex-col items-center mb-6">
          {config.icon}
          <h2 className="text-xl font-bold mt-3 text-[#2D2D2D]">第一幕结束</h2>
          <p className="text-sm text-[#6B7280] mt-1">{config.label}</p>
        </div>

        {/* Family attitudes */}
        <div className="space-y-2 mb-6">
          <h3 className="text-xs font-medium text-[#9CA3AF] uppercase tracking-wider">亲友态度</h3>
          {Object.entries(result.familyAttitudes).map(([id, val]) => {
            const member = [...root.familyA, ...root.familyB].find(f => f.id === id);
            if (!member) return null;
            const pct = Math.max(0, Math.min(100, val + 50));
            return (
              <div key={id} className="flex items-center gap-2">
                <span className="text-xs text-[#6B5B4F] w-16 truncate">{member.name}</span>
                <div className="flex-1 h-2 bg-white/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${val >= 20 ? 'bg-green-400' : val >= 0 ? 'bg-amber-400' : 'bg-red-400'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className={`text-xs font-medium w-8 text-right ${val >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {val > 0 ? '+' : ''}{val}
                </span>
              </div>
            );
          })}
        </div>

        {/* Highlights */}
        {result.highlights.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-medium text-[#9CA3AF] uppercase tracking-wider mb-2">高光时刻</h3>
            <div className="space-y-1.5">
              {result.highlights.map((h, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Heart size={10} className="text-coral-400 mt-1 shrink-0" />
                  <span className="text-xs text-[#6B5B4F]">{h}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Penalties (if barely passed) */}
        {result.outcome === '勉强通关' && (
          <div className="mb-6 p-3 bg-amber-100/50 rounded-xl">
            <p className="text-[10px] text-amber-700">
              勉强通关惩罚：舒适度 -{result.comfortPenalty}，卡牌效果 ×{1 - result.cardEffectReduction}
            </p>
          </div>
        )}

        {/* Continue button */}
        <button
          onClick={handleContinue}
          className={`w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all ${
            canContinue
              ? 'bg-gradient-to-r from-coral-500 to-amber-accent hover:from-coral-700 hover:to-coral-500 shadow-md hover:shadow-lg'
              : 'bg-gradient-to-r from-gray-400 to-gray-500'
          }`}
        >
          {canContinue ? (
            <>进入第二幕 <ArrowRight size={16} /></>
          ) : (
            '返回主菜单'
          )}
        </button>
      </div>
    </div>
  );
};

export default Act1ResultScreen;
